import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, BarChart, Smartphone, Receipt, Settings, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: CreditCard,
    title: 'Öflugt kassakerfi',
    description: 'Notendavænt og sveigjanlegt POS kerfi fyrir alla starfsemi.'
  },
  {
    icon: BarChart,
    title: 'Sölugreining',
    description: 'Ítarleg greining á sölu og birgðum í rauntíma.'
  },
  {
    icon: Smartphone,
    title: 'Farsímavænt',
    description: 'Aðgengilegt í öllum tækjum, þar með talið spjaldtölvum.'
  },
  {
    icon: Receipt,
    title: 'Kvittanir',
    description: 'Rafrænar kvittanir og tengingar við bókhald.'
  },
  {
    icon: Settings,
    title: 'Sérsniðin virkni',
    description: 'Aðlagað að þínum þörfum og rekstri.'
  },
  {
    icon: Shield,
    title: 'Öryggi',
    description: 'Örugg meðhöndlun greiðslna og gagna.'
  }
];

const POS = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Kassakerfi fyrir nútíma rekstur
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Öflugt og notendavænt kassakerfi sem einfaldar þinn rekstur og 
              eykur sölu.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð í kassakerfi
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Allt sem þú þarft í kassakerfi
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildarlausn fyrir kassakerfi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Benefits Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Kostir kerfisins
            </h2>
            <p className="text-xl text-gray-600">
              Helstu kostir við að nota okkar kassakerfi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Einfaldur rekstur
              </h3>
              <p className="text-gray-600">
                Notendavænt viðmót sem krefst lítillar þjálfunar og einfaldar 
                daglegan rekstur.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Betri yfirsýn
              </h3>
              <p className="text-gray-600">
                Nákvæm sölugreining og skýrslur gefa betri yfirsýn yfir 
                reksturinn.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Aukið öryggi
              </h3>
              <p className="text-gray-600">
                Örugg meðhöndlun greiðslna og aðgangsstýring fyrir starfsfólk.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white rounded-xl p-8 shadow-sm"
              whileHover={{ y: -5 }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Sveigjanleiki
              </h3>
              <p className="text-gray-600">
                Kerfið vex með þínum rekstri og aðlagast breyttum þörfum.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að uppfæra kassakerfið?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að setja upp kassakerfi sem eykur 
              skilvirkni og sölu
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð í kassakerfi
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default POS;